import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import { ImageField } from "@/components/MediaUpload";
import { Save, Loader2, PlayCircle, Truck, Plus, Trash2, MapPin, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical capability flags (mirror of backend couriers.CAPABILITIES)
const CAPABILITY_FLAGS = [
  ["rate_calculator", "Rate calculator"], ["pincode_serviceability", "Pincode serviceability"],
  ["create_forward", "Create forward shipment"], ["awb_generation", "AWB generation"],
  ["label", "Shipping label"], ["pickup_registration", "Pickup registration"],
  ["tracking_history", "Tracking history"], ["current_status", "Current status"],
  ["cancellation", "Cancellation"], ["reverse_shipment", "Reverse shipment"],
  ["rto", "RTO handling"], ["reattempt", "Delivery re-attempt"],
  ["change_address", "Change delivery address"], ["change_phone", "Change phone"],
  ["lat_lng", "Latitude / longitude"], ["cod", "COD"], ["prepaid", "Prepaid"],
];

const AUTH_TYPES = [
  ["email_password", "Username / Password → Token"],
  ["signature", "Signature Header"],
  ["api_key", "API Key"],
  ["bearer", "Bearer Token"],
  ["custom_header", "Custom Header"],
];

const Field = ({ label, help, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">{label}</span>
    <div className="mt-1.5">{children}</div>
    {help && <p className="text-[11px] text-[var(--hk-ink-3)] mt-1">{help}</p>}
  </label>
);
const TextInput = (props) => <input {...props} className={"w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] " + (props.className || "")} />;
const NumberInput = ({ value, onChange }) => <input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]" />;
const Toggle = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2.5 cursor-pointer" data-testid={testId}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" data-testid={testId ? `${testId}-input` : undefined} />
    <span className="text-[13.5px]">{label}</span>
  </label>
);

const selectCls = "w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]";

const AdminLogisticsPage = () => {
  const pw = useAdminAuthed();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);        // provider id being tested
  const [testResults, setTestResults] = useState({});  // id -> {ok, message}
  const [addOpen, setAddOpen] = useState(false);
  const [newProv, setNewProv] = useState({ name: "", slug: "", api_style: "shiprocket", auth_type: "email_password", api_base: "", test_mode: true, logo: "", return_location: "", volumetric_divisor: 5000, capabilities: {} });
  const [capsOpen, setCapsOpen] = useState(null); // provider id with capabilities editor open
  const [effCaps, setEffCaps] = useState({});     // provider id -> effective caps (adapter defaults + overrides)
  const [creatingWarehouse, setCreatingWarehouse] = useState(null); // provider id being warehouse-created

  useEffect(() => {
    if (!pw) return;
    axios.get(`${API}/admin/settings/logistics`, { headers: adminHeaders() })
      .then((r) => setData(r.data || {})).catch(() => { toast.error("Failed to load logistics"); setData({}); });
    axios.get(`${API}/admin/logistics/provider-caps`, { headers: adminHeaders() })
      .then((r) => setEffCaps(r.data || {})).catch(() => {});
  }, [pw]);

  if (!data) return <AdminLayout title="Shipping Providers"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;

  const providers = data.providers || {};
  const setActive = (id) => setData({ ...data, active: id });
  const setProviderField = (id, field, value) =>
    setData({ ...data, providers: { ...providers, [id]: { ...providers[id], [field]: value } } });

  const save = async () => {
    setSaving(true);
    // Read the latest state through a functional update so a rapid
    // toggle-then-save click can never PUT a stale `data` snapshot.
    const latest = await new Promise((resolve) => setData((d) => { resolve(d); return d; }));
    try {
      const r = await axios.put(`${API}/admin/settings/logistics`, latest, { headers: adminHeaders() });
      setData(r.data);
      toast.success("Shipping settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const testProvider = async (id) => {
    setTesting(id);
    try {
      const r = await axios.post(`${API}/admin/logistics/test-connection`, {}, { headers: adminHeaders(), params: { provider: id } });
      setTestResults((m) => ({ ...m, [id]: r.data }));
    } catch (e) {
      setTestResults((m) => ({ ...m, [id]: { ok: false, message: e?.response?.data?.detail || "Test failed" } }));
    } finally { setTesting(null); }
  };

  const createWarehouse = async (id) => {
    // Save first so the API call uses the latest address/pincode/phone values
    const latest = await new Promise((resolve) => setData((d) => { resolve(d); return d; }));
    setCreatingWarehouse(id);
    try {
      await axios.put(`${API}/admin/settings/logistics`, latest, { headers: adminHeaders() });
      const r = await axios.post(`${API}/admin/logistics/warehouse/create`, {}, { headers: adminHeaders(), params: { provider: id } });
      setProviderField(id, "pickup_address_id", String(r.data.pickup_address_id || ""));
      toast.success(`Warehouse created — pickup address ID ${r.data.pickup_address_id} saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Warehouse creation failed");
    } finally { setCreatingWarehouse(null); }
  };

  const addProvider = async () => {
    const name = (newProv.name || "").trim();
    if (!name) { toast.error("Give the provider a name"); return; }
    const id = ((newProv.slug || "").trim() || name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (!id || providers[id]) { toast.error("A provider with this code already exists"); return; }
    const style = newProv.api_style;
    const cfg = {
      display_name: name, builtin: false, enabled: false, test_mode: !!newProv.test_mode,
      api_style: style,
      auth_type: newProv.auth_type || (style === "fship" ? "signature" : style === "goswift" ? "goswift" : "email_password"),
      api_base: (newProv.api_base || "").trim() || (style === "fship" ? "https://capi.fship.in" : style === "goswift" ? "https://app.goswift.in" : ""),
      logo: newProv.logo || "",
      email: "", password: "", signature: "", api_key: "", access_token: "",
      custom_header_name: "", custom_header_value: "",
      pickup_pincode: "", warehouse: "", pickup_address_id: "",
      return_location: newProv.return_location || "",
      volumetric_divisor: Number(newProv.volumetric_divisor) || 5000,
      capabilities: newProv.capabilities || {},
    };
    const next = { ...data, providers: { ...providers, [id]: cfg } };
    setData(next);
    setAddOpen(false);
    setNewProv({ name: "", slug: "", api_style: "shiprocket", auth_type: "email_password", api_base: "", test_mode: true, logo: "", return_location: "", volumetric_divisor: 5000, capabilities: {} });
    try {
      await axios.put(`${API}/admin/settings/logistics`, next, { headers: adminHeaders() });
      toast.success(`${name} added — now fill in its credentials below and enable it.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save provider"); }
  };

  const removeProvider = async (id) => {
    if (providers[id]?.builtin) return;
    const nextProviders = { ...providers };
    delete nextProviders[id];
    const next = { ...data, providers: nextProviders, active: data.active === id ? "in-house" : data.active };
    setData(next);
    try {
      await axios.put(`${API}/admin/settings/logistics`, next, { headers: adminHeaders() });
      toast.success("Provider removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <AdminLayout
      title="Shipping Providers"
      subtitle="Shiprocket, Fship, or any compatible courier — the Active provider fulfils paid orders"
      actions={
        <button onClick={save} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="logistics-save">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
        </button>
      }
    >
      <div className="hk-card p-5 mb-4 max-w-3xl">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Active provider (used for new shipments)">
            <select value={data.active || "in-house"} onChange={(e) => setActive(e.target.value)}
              className={selectCls} data-testid="logistics-active">
              {Object.keys(providers).map((k) => <option key={k} value={k}>{providers[k].display_name || k}</option>)}
            </select>
          </Field>
          <div className="pt-6 space-y-3">
            <Toggle checked={!!data.auto_create_shipment} onChange={(v) => setData({ ...data, auto_create_shipment: v })}
              label="Auto-create shipment after successful payment" testId="auto-ship-toggle" />
            <div className="flex items-center gap-3 flex-wrap">
              <Toggle checked={!!data.auto_select_courier} onChange={(v) => setData({ ...data, auto_select_courier: v })}
                label="Auto-select best courier from live rates" testId="auto-select-toggle" />
              {!!data.auto_select_courier && (
                <select value={data.auto_select_strategy || "cheapest"} onChange={(e) => setData({ ...data, auto_select_strategy: e.target.value })}
                  className="px-2 py-1.5 rounded-md border border-[var(--hk-border)] bg-white/70 text-[12.5px]" data-testid="auto-select-strategy">
                  <option value="cheapest">Cheapest</option>
                  <option value="fastest">Fastest</option>
                  <option value="balanced">Balanced</option>
                </select>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 p-4 rounded-lg border border-[var(--hk-border)] bg-[var(--hk-warm-ivory)]/60" data-testid="delivery-staff-card">
          <h3 className="hk-h5 mb-1">Delivery staff page</h3>
          <p className="text-[12px] text-[var(--hk-ink-2)] mb-3 leading-relaxed">
            A simple mobile page at <a href="/delivery" target="_blank" rel="noreferrer" className="underline text-[var(--hk-orange-deep)]">/delivery</a> where
            your delivery team sees pending shipments with one-tap Google Maps navigation and can mark deliveries done — no admin password needed, just this PIN.
          </p>
          <div className="grid md:grid-cols-2 gap-4 items-end">
            <Field label="Staff PIN (keep it simple, e.g. 4-6 digits)">
              <TextInput value={data.delivery_pin || ""} onChange={(e) => setData({ ...data, delivery_pin: e.target.value })} />
            </Field>
            <div className="pb-1">
              <Toggle checked={!!data.delivery_enabled} onChange={(v) => setData({ ...data, delivery_enabled: v })}
                label="Enable delivery staff page" testId="delivery-staff-toggle" />
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Pickup address"><TextInput value={data.pickup_address || ""} onChange={(e) => setData({ ...data, pickup_address: e.target.value })} /></Field>
          <Field label="Pickup pincode"><TextInput value={data.pickup_pincode || ""} onChange={(e) => setData({ ...data, pickup_pincode: e.target.value })} /></Field>
          <Field label="Default weight (g)" help="Used for products without their own weight"><NumberInput value={data.default_weight_g} onChange={(v) => setData({ ...data, default_weight_g: v })} /></Field>
          <Field label="Default dimensions L×W×H (cm)">
            <div className="grid grid-cols-3 gap-2">
              <NumberInput value={data.default_length_cm} onChange={(v) => setData({ ...data, default_length_cm: v })} />
              <NumberInput value={data.default_width_cm} onChange={(v) => setData({ ...data, default_width_cm: v })} />
              <NumberInput value={data.default_height_cm} onChange={(v) => setData({ ...data, default_height_cm: v })} />
            </div>
          </Field>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(providers).map(([id, cfg]) => {
          // GoSwift is detected by id/style too, so an older custom "GoSwift" entry still gets the right credential fields
          const authType = (id === "goswift" || cfg.api_style === "goswift") ? "goswift"
            : cfg.auth_type || (id === "fship" ? "signature" : id === "delhivery" ? "api_key" : id === "in-house" ? "none" : "email_password");
          const tr = testResults[id];
          const isInHouse = cfg.api_style === "in-house" || id === "in-house";
          return (
            <div key={id} className="hk-card p-5" data-testid={`prov-${id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Truck size={16} className="text-[var(--hk-orange-deep)]" />
                <h3 className="hk-h5">{cfg.display_name || id}</h3>
                {id === data.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--hk-orange-deep)] text-white uppercase tracking-widest">Active</span>}
                {cfg.last_test_at && (
                  <span className={"text-[10px] px-2 py-0.5 rounded-full border " + (cfg.last_test_ok ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-red-300 text-red-600 bg-red-50")}
                    title={cfg.last_error || ""} data-testid={`prov-${id}-conn-status`}>
                    {cfg.last_test_ok ? "Connected" : "Connection error"} · {new Date(cfg.last_test_at).toLocaleDateString("en-IN")}
                  </span>
                )}
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[var(--hk-border)]">{cfg.test_mode ? "Test mode" : "Live"}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[var(--hk-border)] text-[var(--hk-ink-3)]">{cfg.api_style === "fship" ? "Fship API" : cfg.api_style === "goswift" ? "GoSwift API" : isInHouse ? "Local" : "Shiprocket-compatible"}</span>
                {!cfg.builtin && (
                  <button onClick={() => removeProvider(id)} className="ml-auto text-[11px] text-red-600 flex items-center gap-1 hover:underline" data-testid={`prov-${id}-delete`}>
                    <Trash2 size={11} /> Remove
                  </button>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Toggle checked={cfg.enabled} onChange={(v) => setProviderField(id, "enabled", v)} label="Enable this provider" testId={`prov-${id}-enable`} />
                <Toggle checked={cfg.test_mode} onChange={(v) => setProviderField(id, "test_mode", v)} label="Test mode" testId={`prov-${id}-test`} />

                {isInHouse && cfg.note && (
                  <div className="md:col-span-2 text-[12.5px] text-[var(--hk-ink-2)] p-3 bg-[var(--hk-warm-ivory)] rounded-md">{cfg.note}</div>
                )}

                {!isInHouse && (
                  <>
                    {authType === "email_password" && (
                      <>
                        <div className="md:col-span-2"><Field label="API Email" help="Shiprocket: Settings › API › API user email"><TextInput value={cfg.email || ""} onChange={(e) => setProviderField(id, "email", e.target.value)} data-testid={`prov-${id}-email`} /></Field></div>
                        <div className="md:col-span-2"><Field label="API Password" help="Stored server-side only — never sent to browsers"><TextInput type="password" value={cfg.password || ""} onChange={(e) => setProviderField(id, "password", e.target.value)} data-testid={`prov-${id}-password`} /></Field></div>
                      </>
                    )}
                    {authType === "signature" && (
                      <>
                        <div className="md:col-span-2"><Field label="API Signature" help="Fship dashboard › API access token"><TextInput type="password" value={cfg.signature || ""} onChange={(e) => setProviderField(id, "signature", e.target.value)} data-testid={`prov-${id}-signature`} /></Field></div>
                        <Field label="Pickup Location Name (nickname)" help="The address nickname from your Fship dashboard — e.g. WH1. Display/reference only.">
                          <TextInput value={cfg.warehouse || ""} onChange={(e) => setProviderField(id, "warehouse", e.target.value)} data-testid={`prov-${id}-warehouse`} />
                        </Field>
                        <Field label="Pickup Address ID (numeric)" help="Paste the NUMERIC pickupaddressid from Fship dashboard › Manage Warehouse (NOT the nickname like WH1) — or leave blank: the warehouse is auto-created via the Fship API on the first shipment, using the pickup address/pincode and warehouse phone below.">
                          <TextInput value={cfg.pickup_address_id || ""} onChange={(e) => setProviderField(id, "pickup_address_id", e.target.value)} data-testid={`prov-${id}-pickup-id`} />
                        </Field>
                        <Field label="Warehouse contact phone" help="Required for API warehouse auto-creation"><TextInput value={cfg.warehouse_phone || ""} onChange={(e) => setProviderField(id, "warehouse_phone", e.target.value)} data-testid={`prov-${id}-warehouse-phone`} /></Field>
                        <Field label="Warehouse city"><TextInput value={cfg.warehouse_city || ""} onChange={(e) => setProviderField(id, "warehouse_city", e.target.value)} data-testid={`prov-${id}-warehouse-city`} /></Field>
                        <div className="md:col-span-2">
                          <button type="button" onClick={() => createWarehouse(id)} disabled={creatingWarehouse === id} className="hk-btn-ghost h-9 px-4 text-[12.5px] inline-flex items-center gap-2" data-testid={`prov-${id}-create-warehouse`}>
                            {creatingWarehouse === id ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Create warehouse via API & save ID
                          </button>
                          <p className="text-[11px] text-[var(--hk-ink-3)] mt-1">Calls Fship addwarehouse with the pickup address/pincode above and stores the returned numeric pickup address ID automatically.</p>
                        </div>
                      </>
                    )}
                    {authType === "goswift" && (
                      <>
                        <div className="md:col-span-2 text-[12.5px] text-[var(--hk-ink-2)] p-3 bg-[var(--hk-warm-ivory)] rounded-md" data-testid={`prov-${id}-note`}>
                          {cfg.note || "Credentials are issued by GoSwift during onboarding."} Token (24h) is fetched and reused automatically.
                        </div>
                        <div className="md:col-span-2"><Field label="GoSwift Client ID" help="Provided by GoSwift during onboarding (used in the auth URL)"><TextInput value={cfg.client_id || ""} onChange={(e) => setProviderField(id, "client_id", e.target.value)} className="font-mono text-[12.5px]" data-testid={`prov-${id}-client-id`} /></Field></div>
                        <Field label="Username"><TextInput value={cfg.username || ""} onChange={(e) => setProviderField(id, "username", e.target.value)} data-testid={`prov-${id}-username`} /></Field>
                        <Field label="Password" help="Stored server-side only — never sent to browsers"><TextInput type="password" value={cfg.password || ""} onChange={(e) => setProviderField(id, "password", e.target.value)} data-testid={`prov-${id}-password`} /></Field>
                        <Field label="Pickup location alias" help="Leave blank if only ONE pickup address is registered with GoSwift (auto-filled). Otherwise the alias from your Swift dashboard.">
                          <TextInput value={cfg.warehouse || ""} onChange={(e) => setProviderField(id, "warehouse", e.target.value)} data-testid={`prov-${id}-warehouse`} />
                        </Field>
                        <Field label="Service type">
                          <select value={cfg.service || "SURFACE"} onChange={(e) => setProviderField(id, "service", e.target.value)} className="hk-input h-10 w-full" data-testid={`prov-${id}-service`}>
                            {["SURFACE", "EXPRESS", "NDD", "SDD"].map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </Field>
                        <Field label="GST rate % (for tax split)" help="GoSwift needs taxable + tax split. 12% is typical for Ayurvedic products; set 0 if exempt."><NumberInput value={cfg.gst_rate_pct ?? 12} onChange={(v) => setProviderField(id, "gst_rate_pct", v)} /></Field>
                        <Field label="HSN code (optional)"><TextInput value={cfg.hsn_code || ""} onChange={(e) => setProviderField(id, "hsn_code", e.target.value)} data-testid={`prov-${id}-hsn`} /></Field>
                        <Field label="Seller legal name" help="Falls back to Invoice settings › Company name"><TextInput value={cfg.seller_name || ""} onChange={(e) => setProviderField(id, "seller_name", e.target.value)} /></Field>
                        <Field label="Seller GSTIN" help="Required by GoSwift. Falls back to Invoice settings › GSTIN"><TextInput value={cfg.seller_gst_tin || ""} onChange={(e) => setProviderField(id, "seller_gst_tin", e.target.value)} className="font-mono text-[12.5px]" data-testid={`prov-${id}-gstin`} /></Field>
                        <div className="md:col-span-2"><Field label="Seller address" help="Printed on the label. Falls back to Invoice settings › Address"><TextInput value={cfg.seller_address || ""} onChange={(e) => setProviderField(id, "seller_address", e.target.value)} /></Field></div>
                        <Field label="Category of goods"><TextInput value={cfg.category_of_goods || ""} onChange={(e) => setProviderField(id, "category_of_goods", e.target.value)} /></Field>
                      </>
                    )}
                    {authType === "api_key" && (
                      <div className="md:col-span-2"><Field label="API Key / Token"><TextInput type="password" value={cfg.api_key || ""} onChange={(e) => setProviderField(id, "api_key", e.target.value)} data-testid={`prov-${id}-apikey`} /></Field></div>
                    )}
                    <Field label="Base API URL"><TextInput value={cfg.api_base || ""} onChange={(e) => setProviderField(id, "api_base", e.target.value)} className="font-mono text-[12.5px]" data-testid={`prov-${id}-base`} /></Field>
                    {authType !== "signature" && authType !== "goswift" && (
                      <Field label="Pickup location name" help="Must EXACTLY match the pickup location in your courier panel (Shiprocket: Settings › Pickup Address)">
                        <TextInput value={cfg.warehouse || ""} onChange={(e) => setProviderField(id, "warehouse", e.target.value)} data-testid={`prov-${id}-warehouse`} />
                      </Field>
                    )}
                    <Field label="Pickup pincode override"><TextInput value={cfg.pickup_pincode || ""} onChange={(e) => setProviderField(id, "pickup_pincode", e.target.value)} /></Field>
                    <Field label="Return location"><TextInput value={cfg.return_location || ""} onChange={(e) => setProviderField(id, "return_location", e.target.value)} /></Field>
                    <Field label="Volumetric divisor" help="L×B×H ÷ divisor; Fship documents 5000"><NumberInput value={cfg.volumetric_divisor ?? 5000} onChange={(v) => setProviderField(id, "volumetric_divisor", v)} /></Field>
                  </>
                )}
              </div>

              {!isInHouse && (
                <div className="mt-3">
                  <button type="button" onClick={() => setCapsOpen(capsOpen === id ? null : id)}
                    className="flex items-center gap-1.5 text-[12px] text-[var(--hk-ink-2)] hover:text-[var(--hk-ink)]" data-testid={`prov-${id}-caps-toggle`}>
                    {capsOpen === id ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Capabilities
                    <span className="text-[10px] text-[var(--hk-ink-3)]">(adapter defaults apply unless overridden)</span>
                  </button>
                  {capsOpen === id && (
                    <div className="grid sm:grid-cols-3 gap-y-1.5 gap-x-4 mt-2 p-3 rounded-md bg-[var(--hk-warm-ivory)]/60 border border-[var(--hk-border)]" data-testid={`prov-${id}-caps`}>
                      {CAPABILITY_FLAGS.map(([k, label]) => {
                        // Checkbox reflects the EFFECTIVE capability (adapter defaults + overrides)
                        const effective = (effCaps[id] || {})[k];
                        const overridden = k in (cfg.capabilities || {});
                        return (
                          <label key={k} className="flex items-center gap-2 text-[12px] cursor-pointer" data-testid={`prov-${id}-cap-${k}`}>
                            <input type="checkbox" checked={!!effective}
                              onChange={(e) => {
                                const next = { ...(effCaps[id] || {}), ...(cfg.capabilities || {}), [k]: e.target.checked };
                                setProviderField(id, "capabilities", next);
                                setEffCaps((m) => ({ ...m, [id]: { ...(m[id] || {}), [k]: e.target.checked } }));
                              }}
                              className="w-3.5 h-3.5" />
                            {label}{overridden ? "" : <span className="text-[9px] text-[var(--hk-ink-3)]">(default)</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {!isInHouse && (
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <button onClick={() => testProvider(id)} disabled={testing === id}
                    className="hk-btn-ghost h-9 px-3 text-[12px]" data-testid={`prov-${id}-test-btn`}>
                    {testing === id ? <Loader2 className="animate-spin" size={13} /> : <PlayCircle size={13} />} Test Connection
                  </button>
                  <span className="text-[11px] text-[var(--hk-ink-3)]">Save first — the test uses your saved credentials.</span>
                  {tr && (
                    <span className={"text-[12px] " + (tr.ok ? "text-emerald-700" : "text-red-600")} data-testid={`prov-${id}-test-result`}>
                      {tr.ok ? "✓ " : "✗ "}{tr.message}
                    </span>
                  )}
                  {tr?.debug && (
                    <details className="w-full text-[11px] font-mono bg-[var(--hk-warm-ivory)]/70 border border-[var(--hk-border)] rounded-md p-2.5" data-testid={`prov-${id}-test-debug`}>
                      <summary className="cursor-pointer text-[var(--hk-ink-2)]">Connection debug (no secrets)</summary>
                      <div className="mt-1.5 space-y-0.5 text-[var(--hk-ink-2)]">
                        <p>environment: {tr.debug.environment}</p>
                        <p>base_url: {tr.debug.base_url}</p>
                        <p>endpoint: {tr.debug.endpoint}</p>
                        <p>http_status: {tr.debug.http_status ?? "—"}</p>
                        <p>signature_header_present: {String(tr.debug.signature_header_present)}</p>
                        {tr.debug.response_body && <p className="break-all">response: {tr.debug.response_body}</p>}
                      </div>
                    </details>
                  )}
                  {!tr && cfg.last_error && (
                    <span className="text-[12px] text-red-600" data-testid={`prov-${id}-last-error`}>✗ Last error: {cfg.last_error}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Add custom provider */}
        <div className="hk-card p-5" data-testid="add-provider-card">
          {!addOpen ? (
            <button onClick={() => setAddOpen(true)} className="flex items-center gap-2 text-[13.5px] text-[var(--hk-orange-deep)] font-medium" data-testid="add-provider-open">
              <Plus size={15} /> Add a custom shipping provider
            </button>
          ) : (
            <div className="space-y-4">
              <h3 className="hk-h5">Add logistics provider</h3>
              <p className="text-[12px] text-[var(--hk-ink-2)]">
                Configuration only — no code changes. Pick the API style the provider follows; the matching adapter handles the wire format.
                Credentials are stored server-side and masked on read.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Provider name"><TextInput value={newProv.name} onChange={(e) => setNewProv({ ...newProv, name: e.target.value })} placeholder="e.g. Shadowfax" data-testid="add-provider-name" /></Field>
                <Field label="Provider code / slug" help="Auto from name if blank"><TextInput value={newProv.slug} onChange={(e) => setNewProv({ ...newProv, slug: e.target.value })} placeholder="shadowfax" data-testid="add-provider-slug" /></Field>
                <Field label="Environment">
                  <select value={newProv.test_mode ? "test" : "live"} onChange={(e) => setNewProv({ ...newProv, test_mode: e.target.value === "test" })} className={selectCls} data-testid="add-provider-env">
                    <option value="test">Test / Staging</option>
                    <option value="live">Production / Live</option>
                  </select>
                </Field>
                <Field label="API style (adapter)">
                  <select value={newProv.api_style} onChange={(e) => setNewProv({ ...newProv, api_style: e.target.value, auth_type: e.target.value === "fship" ? "signature" : "email_password" })} className={selectCls} data-testid="add-provider-style">
                    <option value="shiprocket">Shiprocket-compatible (login → token)</option>
                    <option value="fship">Fship-compatible (signature header)</option>
                    <option value="goswift">GoSwift-compatible (client id + username/password token)</option>
                  </select>
                </Field>
                <Field label="Authentication type">
                  <select value={newProv.auth_type} onChange={(e) => setNewProv({ ...newProv, auth_type: e.target.value })} className={selectCls} data-testid="add-provider-auth">
                    {AUTH_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Base API URL"><TextInput value={newProv.api_base} onChange={(e) => setNewProv({ ...newProv, api_base: e.target.value })} placeholder="https://…" className="font-mono text-[12.5px]" data-testid="add-provider-base" /></Field>
                <Field label="Return location (optional)"><TextInput value={newProv.return_location} onChange={(e) => setNewProv({ ...newProv, return_location: e.target.value })} data-testid="add-provider-return" /></Field>
                <Field label="Volumetric divisor" help="Fship documents L×B×H ÷ 5000"><NumberInput value={newProv.volumetric_divisor} onChange={(v) => setNewProv({ ...newProv, volumetric_divisor: v })} /></Field>
                <Field label="Logo (optional)">
                  <ImageField value={newProv.logo} onChange={(v) => setNewProv({ ...newProv, logo: v })} label="" testid="add-provider-logo" />
                </Field>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Capabilities (leave all off to use adapter defaults)</div>
                <div className="grid sm:grid-cols-3 gap-y-1.5 gap-x-4">
                  {CAPABILITY_FLAGS.map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-[12.5px] cursor-pointer" data-testid={`add-provider-cap-${k}`}>
                      <input type="checkbox" checked={!!(newProv.capabilities || {})[k]}
                        onChange={(e) => setNewProv({ ...newProv, capabilities: { ...(newProv.capabilities || {}), [k]: e.target.checked } })}
                        className="w-4 h-4" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={addProvider} className="hk-btn-primary h-10 px-4" data-testid="add-provider-save">Add provider</button>
                <button onClick={() => setAddOpen(false)} className="hk-btn-ghost h-10 px-4">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminLogisticsPage;
